export interface SmriqJourneyViewStateInput {
    hasContext: boolean;
    contextDurationDefault: number | null | undefined;
    contextMotDefault: boolean | null | undefined;
    isContextLoading: boolean;
    canAutoConfirm: boolean;
    confirmed: boolean;
    configuredServiceCount: number;
    planSettingsKnown: boolean;
    hasPersistedPlanSettings: boolean;
    needsReconfirm: boolean;
    confirmError: unknown;
    generateError: unknown;
    showVehicleForm: boolean;
    isGenerating: boolean;
    hasPlan: boolean;
    editPlanRequested: boolean;
    /** Stale for a reason the plan controls must show; a last-service change alone is not one. */
    isStale: boolean;
    /** The preview or an incomplete selection blocks generation, so no automatic journey is coming. */
    generationBlocked?: boolean;
}

export interface SmriqJourneyViewState {
    autoDefaultsReady: boolean;
    autoJourneyProducesPlan: boolean;
    retrievingPlan: boolean;
    planLoading: boolean;
    planSettingsKnownOrRestored: boolean;
    showPlanControls: boolean;
}

export function deriveSmriqJourneyViewState(
    input: SmriqJourneyViewStateInput
): SmriqJourneyViewState {
    // While the on-load automatic journey runs, a single full-panel loader stands in for every
    // other control so the dealer sees "Retrieving Plan..." rather than a flicker of partial UI.
    const autoDefaultsReady =
        input.hasContext && input.contextDurationDefault != null && input.contextMotDefault != null;
    const autoJourneyProducesPlan =
        !input.generationBlocked &&
        (input.canAutoConfirm || input.confirmed) &&
        (input.isContextLoading || autoDefaultsReady);
    const retrievingPlan =
        (autoJourneyProducesPlan &&
            input.configuredServiceCount === 0 &&
            !input.planSettingsKnown &&
            !input.needsReconfirm &&
            !input.confirmError &&
            !input.generateError &&
            !input.showVehicleForm) ||
        // A reconfirm cleared the plan and is regenerating it; stand the loader in for the
        // whole panel rather than flashing empty controls until the schedule returns.
        (input.isGenerating &&
            input.configuredServiceCount === 0 &&
            !input.showVehicleForm &&
            !input.generateError);

    // Deliberately NOT the toggle debounce window (that keeps isGenerating false), so the
    // dealer can keep toggling items before the plan re-prices.
    const planLoading = retrievingPlan || input.isGenerating;

    // Known either from a generate this session or from those the quote restored, so a resumed
    // plan collapses to the summary and pre-fills the form exactly like a fresh one.
    const planSettingsKnownOrRestored = input.planSettingsKnown || input.hasPersistedPlanSettings;

    // An auto-applied toggle does NOT set isStale, so it regenerates with the controls
    // collapsed and an updating hint.
    const showPlanControls =
        !input.hasPlan ||
        input.editPlanRequested ||
        input.needsReconfirm ||
        input.isStale ||
        !!input.generateError ||
        !planSettingsKnownOrRestored;

    return {
        autoDefaultsReady,
        autoJourneyProducesPlan,
        retrievingPlan,
        planLoading,
        planSettingsKnownOrRestored,
        showPlanControls,
    };
}
